import { useEffect, useRef, useState } from "react";
import { socialProofs } from "../../../mocks/home";

const REAL_PHOTO_2 = "https://storage.readdy-site.link/project_files/1dd47190-5ea2-431c-a9ac-432354462ac6/75ebc5fc-d2a0-478f-9d77-eba8f02c82c5_64179567944dbb89ce0fd9b626d3393fd094e8a8.png?v=2ddda1c25cf8eccfdd1662238c56bf29";

export default function SocialProofSection() {
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setActive((p) => (p + 1) % socialProofs.length), 4000);
    return () => clearInterval(timer);
  }, []);

  const current = socialProofs[active];

  return (
    <section ref={ref} className="bg-white py-24 lg:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left Text */}
          <div
            className={`flex-1 transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <span className="text-xs font-semibold tracking-widest text-[#191F28]/40 uppercase mb-4 block">
              Social Proof
            </span>
            <h2 className="text-4xl lg:text-5xl font-black text-[#191F28] leading-relaxed lg:leading-relaxed mb-6">
              실제로 수익을<br />
              <span className="text-[#00D873]">인증하고 있어요</span>
            </h2>
            <p className="text-lg text-[#191F28]/60 mb-10 leading-[1.9]">
              이론이 아닙니다. GrowSeo 유저들이 AI로<br />
              실제 수익과 칼퇴를 만들어내고 있어요.
            </p>

            {/* Avatars */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex -space-x-3">
                {socialProofs.map((p) => (
                  <img
                    key={p.id}
                    src={p.avatar}
                    alt={p.name}
                    className="w-10 h-10 rounded-full border-2 border-white object-cover object-top"
                  />
                ))}
              </div>
              <span className="text-sm text-[#191F28]/60 font-medium">이미 12,400명의 세오님들이 수익을 만들고 있어요</span>
            </div>

            {/* Mini photo strip */}
            <div className="flex gap-3 mb-8">
              <div className="w-20 h-24 rounded-2xl overflow-hidden flex-shrink-0">
                <img
                  src={REAL_PHOTO_2}
                  alt="GrowSeo 현장"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex flex-col justify-center gap-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#00D873] rounded-full" />
                  <span className="text-xs font-semibold text-[#191F28]/60">실제 수강생 촬영</span>
                </div>
                <p className="text-sm font-bold text-[#191F28] leading-normal">
                  진짜 사람들이<br />만드는 성장
                </p>
              </div>
            </div>

            {/* Dots */}
            <div className="flex gap-2">
              {socialProofs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === active ? "w-8 h-2 bg-[#00D873]" : "w-2 h-2 bg-[#F2F4F6]"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right Testimonial Card */}
          <div
            className={`flex-1 transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="relative pb-8 pr-8">
              {/* Main Card */}
              <div className="bg-[#191F28] rounded-3xl p-10 text-white relative overflow-hidden">
                {/* Real photo as subtle bg */}
                <div className="absolute inset-0 pointer-events-none">
                  <img
                    src={REAL_PHOTO_2}
                    alt=""
                    className="w-full h-full object-cover object-top opacity-[0.10]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#191F28]/90 via-[#191F28]/70 to-[#191F28]/90" />
                </div>
                {/* Green accent */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#00D873]/10 rounded-full translate-x-10 -translate-y-10" />

                {/* Quote mark — always visible */}
                <div className="text-6xl font-black text-[#00D873] leading-none mb-6">"</div>

                {/* Animated content — key triggers re-mount on slide change */}
                <div
                  key={active}
                  style={{ animation: "socialCardIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both" }}
                >
                  {/* Review text */}
                  <p className="text-xl font-medium leading-[1.9] mb-10 relative z-10">
                    {current.quote}
                  </p>

                  {/* User info */}
                  <div className="flex items-center gap-4 relative z-10">
                    <img
                      src={current.avatar}
                      alt={current.name}
                      className="w-12 h-12 rounded-full object-cover object-top border-2 border-[#00D873]/30"
                    />
                    <div>
                      <div className="font-bold text-white">{current.name}</div>
                      <div className="text-sm text-white/50">{current.role}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <span className="text-lg">{current.badge}</span>
                      <span className="text-sm font-bold text-[#00D873]">{current.streak}일 연속</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating stat card */}
              <div
                className="absolute -bottom-5 -right-5 bg-white rounded-2xl px-5 py-4 border border-[#F2F4F6] z-20"
                style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.10)" }}
              >
                <div className="text-xs text-[#191F28]/50 mb-1">오늘의 챌린지 완료</div>
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-black text-[#191F28]">3,240</div>
                  <div className="bg-[#00D873]/10 text-[#00D873] text-xs font-bold px-2 py-1 rounded-full">
                    +12%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
